<template>
  <ToolShell
    tool-name="Deposit calculator"
    h1="Deposit calculator for client work."
    lede="A deposit is a share of the project price, usually 30–50%, paid before work starts. Enter the price and your percentage to get the deposit amount, the remaining balance, and a simple billing schedule. Free, instant, no signup."
    :trust="['No signup', 'Instant results', 'Billing schedule included']"
    form-title="Your numbers"
    :upsell-points="[
      'The deposit is requested when the client accepts the proposal',
      'Automated reminders follow up so you don’t have to',
      'Every payment lands on the client record',
    ]"
    free-label="The free calculator"
    :free-bullets="[
      'The deposit and balance for one project, calculated once',
      'You schedule and chase each payment yourself',
      'No record of what’s been collected or what’s left',
    ]"
    upgrade-headline="This number works once. Workspace369 makes it a workflow."
    :connected-bullets="[
      'Deposits and balances bill on a set schedule',
      'Follow-up reminders run automatically with automations',
      'Payment links collect online',
      'Everything lands on the client record — status always visible',
    ]"
    slug="/tools/deposit-calculator"
    :meta-description="metaDescription"
    doc-brand="Deposit calculator"
    :doc-subtitle="scheduleSummary"
    doc-kind="SUMMARY"
    stamp="Result"
  >
    <template #form>
      <label class="ta-field">
        <span>Project price ($)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M11.5 4.5c-.6-1.2-1.9-1.8-3.5-1.8-1.9 0-3.2.9-3.2 2.4 0 3.4 7 1.7 7 5.1 0 1.6-1.5 2.5-3.8 2.5-1.8 0-3.2-.8-3.8-2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.price" type="number" min="0" step="100" placeholder="5000" />
        </div>
      </label>

      <label class="ta-field">
        <span>Deposit percentage (%)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M12.5 3.5l-9 9" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="4.8" cy="4.8" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="11.2" cy="11.2" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>
          <input v-model.number="form.percent" type="number" min="0" max="100" step="5" placeholder="50" />
        </div>
      </label>

      <div class="ta-field">
        <span id="deposit-split-label">Split the balance into</span>
        <ToolDropdown
          v-model="form.installments"
          :options="installmentOptions"
          aria-label="Split the balance into"
          labelledby="deposit-split-label"
        />
      </div>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>Project price</span><strong>{{ money(form.price) }}</strong></div>
        <div><span>Deposit</span><strong>{{ form.percent || 0 }}%</strong></div>
        <div><span>Remaining balance</span><strong>{{ money(balance) }}</strong></div>
      </div>

      <div class="ta-table-scroll" tabindex="0">
        <table class="ta-table">
          <thead>
            <tr><th>Billing schedule</th><th>Amount</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Deposit before work starts</td>
              <td class="ta-num">{{ money(deposit) }}</td>
            </tr>
            <tr v-for="n in form.installments" :key="n">
              <td>Payment {{ n }} of {{ form.installments }}</td>
              <td class="ta-num">{{ money(balance / form.installments) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="ta-doc-total" :class="{ 'ta-doc-total--long': money(deposit).length > 11 }">
        <span>Deposit due up front</span>
        <strong>{{ money(deposit) }}</strong>
      </div>

      <p class="ta-doc-note">Most client-work businesses collect 30–50% up front.</p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Enter price and percentage</h2><p>The project price and how much you want up front — most businesses ask 30–50%.</p></div></div>
      <div><span>02</span><div><h2>Split the balance</h2><p>One payment on completion, or two or three installments along the way.</p></div></div>
      <div><span>03</span><div><h2>Read the schedule</h2><p>The deposit, each installment, and the remaining balance update live as you type.</p></div></div>
    </template>

    <ToolGuide :sections="guideSections" faq-title="Deposit questions" :faq="faq" />

    <RelatedPages path="/tools/deposit-calculator" title="Deposit calculator" />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolGuide from "~/components/tools/ToolGuide.vue";
import ToolShell from "~/components/tools/ToolShell.vue";

const form = reactive({ price: null, percent: 50, installments: 1 });

const installmentOptions = [
  { value: 1, label: "1 payment (on completion)" },
  { value: 2, label: "2 payments" },
  { value: 3, label: "3 payments" },
];

const deposit = computed(() => ((Number(form.price) || 0) * (Number(form.percent) || 0)) / 100);
const balance = computed(() => Math.max(0, (Number(form.price) || 0) - deposit.value));

const money = (value) =>
  `$${(Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const scheduleSummary = computed(
  () => `${money(form.price)} project · ${form.percent || 0}% deposit`,
);

const metaDescription =
  "Free deposit calculator: enter the project price and deposit percentage to get the up-front amount, balance, and a billing schedule. Instant, no signup.";

const guideSections = [
  {
    title: "How deposits are usually set",
    paragraphs: [
      "A deposit is a share of the project price paid before work starts. Most service businesses ask for somewhere between a quarter and a half, because that is enough to show the client is committed and to cover your first stretch of time, without making the up-front ask feel heavy.",
      "There is no single right number. Set it from your own risk: how much time or money you spend before the first milestone, how well you know the client, and how large the project is.",
    ],
    bullets: [
      "Small jobs and new clients: ask for more up front, up to the full price for very short work.",
      "Larger projects: a smaller deposit, then scheduled payments tied to milestones.",
      "Work with up-front costs such as materials, licences or subcontractors: set the deposit to cover them.",
      "Ongoing work: bill a retainer on a fixed date each month instead of a one-off deposit.",
    ],
  },
  {
    title: "Worked examples with this calculator",
    paragraphs: [
      "The calculator takes the deposit as the price times the percentage, then splits what is left evenly across the payments you choose. Here are three runs you can reproduce above.",
    ],
    bullets: [
      "$5,000 project, 50% deposit, balance in 1 payment: the deposit is $2,500.00, the balance is $2,500.00, and the single final payment is $2,500.00.",
      "$8,000 project, 30% deposit, balance in 2 payments: the deposit is $2,400.00, the balance is $5,600.00, and each payment is $2,800.00.",
      "$12,000 project, 25% deposit, balance in 3 payments: the deposit is $3,000.00, the balance is $9,000.00, and each payment is $3,000.00.",
    ],
  },
  {
    title: "Refundable or non-refundable",
    paragraphs: [
      "Whether a deposit can be returned is a matter of what you and the client agreed, so put it in writing before any money moves. A refundable deposit is held against the work and returned if the project does not go ahead for an agreed reason. A non-refundable deposit pays for the time you reserve and the preparation you do, so it is kept if the client cancels.",
      "Say plainly in the proposal which kind it is, what it covers and what happens if either side cancels. Rules on deposits can differ by place and by type of work, so check what applies to you if you are unsure. A kill fee is a separate agreement for cancellation partway through a project.",
    ],
    links: [
      { label: "Kill fee explained", path: "/learn/kill-fee" },
      { label: "Retainer fee explained", path: "/learn/retainer-fee" },
    ],
  },
  {
    title: "How to request a deposit on acceptance",
    paragraphs: [
      "The easiest moment to ask is the moment the client says yes. Put the deposit amount and due date in the proposal, so accepting it also accepts the deposit, then send a deposit invoice straight away with a way to pay.",
    ],
    bullets: [
      "State the deposit percentage, amount and due date in the proposal.",
      "Send the deposit invoice as soon as the proposal is accepted, with the proposal referenced on it.",
      "Start work once the deposit has landed, and say so in your terms.",
      "Show the deposit as a credit on the final invoice so the balance is easy to follow.",
    ],
    links: [
      { label: "Deposit invoice guide", path: "/learn/deposit-invoice" },
      { label: "How to write a proposal", path: "/learn/how-to-write-a-proposal" },
      { label: "Proposals and quotes", path: "/features/quoting" },
    ],
  },
  {
    title: "Collecting deposits in Workspace369",
    paragraphs: [
      "Workspace369 can collect a deposit when a client accepts a proposal, and payment links let the client pay online. Both are on every plan. Splitting the balance into payment plans, with each installment tracked on the invoice, starts on Specialist.",
    ],
  },
];

const faq = [
  {
    q: "What percentage deposit should I ask for?",
    a: "Most client-work businesses ask for between 25% and 50% before work starts. Ask for more on short jobs and new clients, and less on large projects that have milestone payments later. Choose the figure that covers your early costs and time.",
  },
  {
    q: "Is a deposit the same as a retainer?",
    a: "No. A deposit is a one-off part of a project fee, paid up front and credited against the final invoice. A retainer is a recurring payment, usually monthly, that reserves your time or covers an agreed scope for as long as the relationship runs.",
  },
  {
    q: "Should the deposit come off the final invoice?",
    a: "Yes, in almost every case. The deposit is part of the agreed price, not an extra. Show the total fee, the deposit already paid and the balance due on the final invoice so the client can see exactly what remains.",
  },
];

useHead(() => ({
  title: "Free Deposit Calculator for Client Work | Workspace369",
  meta: [
    {
      name: "description",
      content: metaDescription,
    },
    { name: "twitter:title", content: "Free Deposit Calculator | Workspace369" },
    {
      name: "twitter:description",
      content: "Enter the project price and deposit percentage to get the up-front amount, balance, and billing schedule. Free, no signup.",
    },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: "Free Deposit Calculator | Workspace369" },
    {
      property: "og:description",
      content: "Enter the project price and deposit percentage to get the up-front amount, balance, and billing schedule. Free, no signup.",
    },
  ],
}));
</script>
